import type { QueryClient } from '@tanstack/react-query';

import { basepath } from '@/constants';
import {
	APP_KEY_STORAGE_KEY,
	DEXIE_DB_NAME,
	ISSUE_INDEX_DB_NAME,
	QUERY_CACHE_DB_NAME,
	WORKBOX_EXPIRATION_DB_NAME,
	deleteCaches,
	deleteDatabase,
	emptyDatabase,
	isPrecache,
	wipeOrigin,
	type KV,
} from '@shared/storage';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('clear-app-data');

// Set by `clearCache` for the page after the reload, which says it worked.
const CACHE_CLEARED_KEY = 'cf-cache-cleared';

/**
 * Level 1, "Clear cache": everything the app can fetch again -- the query
 * cache and its IndexedDB copy, downloaded issues, the KV cache, and every
 * runtime cache (images, video, audio, fonts, styles) -- then a reload.
 *
 * Kept: everything the reader created or chose -- the sign-in, Settings,
 * favorites, the analytics opt-out, issue languages, reading stats and the
 * event log (unsent events still go out) -- and the debug log, which is what
 * support needs when "clear the cache" didn't help. It touches only the
 * stores named here, so a new setting is never wiped by accident. The
 * precache stays too: it is the app itself.
 *
 * The page's own databases are deleted (that frees their space at once);
 * the two the service worker keeps open are emptied instead, since a delete
 * would wait on its connection and block every later read of them.
 */
export async function clearCache({
	queryClient,
	kv,
}: {
	queryClient: QueryClient;
	kv: KV;
}): Promise<void> {
	kv.clear();
	queryClient.clear();

	const steps = await Promise.allSettled([
		deleteCaches((name) => !isPrecache(name)),
		// Emptied first, so nothing is left if the delete can't run until
		// this page has gone.
		emptyDatabase(QUERY_CACHE_DB_NAME).then(() => deleteDatabase(QUERY_CACHE_DB_NAME)),
		emptyDatabase(DEXIE_DB_NAME).then(() => deleteDatabase(DEXIE_DB_NAME)),
		emptyDatabase(ISSUE_INDEX_DB_NAME),
		emptyDatabase(WORKBOX_EXPIRATION_DB_NAME),
	]);
	for (const step of steps) {
		if (step.status === 'rejected') {
			logger.error('Failed to clear part of the cache', step.reason);
		}
	}

	try {
		sessionStorage.setItem(CACHE_CLEARED_KEY, '1');
	} catch {
		// No notice after the reload; the cache is still cleared.
	}
	window.location.reload();
}

/**
 * Level 2, "Erase all content and settings": the app as if newly
 * installed. Every cache (the precache included), every database,
 * localStorage and sessionStorage -- so the reader is signed out, on this
 * device only -- and the service worker is unregistered. Then the app
 * loads from the network, at its start page. Needs a connection.
 */
export async function eraseAllData(appKey: string): Promise<void> {
	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.allSettled(registrations.map((registration) => registration.unregister()));
		} catch (error) {
			logger.error('Failed to unregister the service worker', error);
		}
	}
	await wipeOrigin();
	try {
		// The app this origin runs is still this one (`hasAppChanged`).
		localStorage.setItem(APP_KEY_STORAGE_KEY, appKey);
	} catch {
		// Recorded again on the next boot.
	}
	window.location.replace(basepath ?? '/');
}

/** True once, on the first load after `clearCache`. */
export function takeCacheClearedNotice(): boolean {
	try {
		const cleared = sessionStorage.getItem(CACHE_CLEARED_KEY) === '1';
		sessionStorage.removeItem(CACHE_CLEARED_KEY);
		return cleared;
	} catch {
		return false;
	}
}
